import type { ReactNode } from 'react';

import type { ColorName } from '@poseidon/components/Input2';
import { Button } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';

import type { CampaignPerson, SenderCount } from './campaignOwner';
import { CampaignSendersNotice } from './CampaignSendersNotice';

type Props = {
  title: string;
  body?: ReactNode;
  owner: CampaignPerson | null;
  acting: CampaignPerson | null;
  /** Whose LinkedIn sends; omitted when the action sends nothing (reject, remove). */
  senders?: readonly SenderCount[];
  confirmLabel: string;
  confirmColor: ColorName;
  onConfirm: () => void;
  onCancel: () => void;
  /** Hold Confirm until the confirm can say everything it should (e.g. sender names still loading). */
  confirmDisabled?: boolean;
  /** While the action runs the confirm stays up and can't be dismissed, so nobody clicks twice. */
  isPending?: boolean;
  testId?: string;
};

/** A confirm for an action on many people at once: says how many, into whose campaign, sent from whom. */
export function BulkCampaignConfirm({
  title,
  body,
  owner,
  acting,
  senders,
  confirmLabel,
  confirmColor,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  isPending = false,
  testId,
}: Props) {
  const close = () => {
    if (!isPending) onCancel();
  };
  return (
    <Popup onClose={close} className="max-w-md text-left">
      <h3 className="text-lg font-semibold">{title}</h3>
      {body != null && <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">{body}</div>}
      <CampaignSendersNotice owner={owner} senders={senders} acting={acting} />
      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={close} disabled={isPending}>
          Cancel
        </Button>
        <Button
          color={confirmColor}
          onClick={onConfirm}
          disabled={confirmDisabled}
          status={isPending || confirmDisabled ? 'updating' : undefined}
          data-testid={testId != null ? `${testId}-confirm` : undefined}>
          {confirmLabel}
        </Button>
      </div>
    </Popup>
  );
}
